import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { useFormSubmit } from '../../hooks/useFormSubmit';

const ROICalculator = () => {
  const [tools, setTools] = useState(15);
  const [avgCost, setAvgCost] = useState(6000);
  const [consolidationRate, setConsolidationRate] = useState(30);

  const [email, setEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const { status, error, submitForm, resetFormStatus } = useFormSubmit();

  const totalCurrentSpend = tools * avgCost;
  const toolsEliminated = Math.round(tools * (consolidationRate / 100));
  const estimatedSavings = toolsEliminated * avgCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !websiteUrl) {
      setShowEmailInput(true);
      return;
    }

    const payload = {
      email,
      websiteUrl,
      tools,
      avgCost,
      consolidationRate,
      estimatedSavings,
      totalCurrentSpend,
      toolsEliminated,
      listName: 'ROI Calculator'
    };

    const success = await submitForm(payload);
    if (success) {
      setEmail('');
      setWebsiteUrl('');
      setShowEmailInput(false);
    }
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 shadow-xl mb-16 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'linear-gradient(to right, #40E0D0 1px, transparent 1px), linear-gradient(to bottom, #40E0D0 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12">

        <div>
          <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-2 flex items-center gap-3">
            <SafeIcon name="DollarSign" className="w-6 h-6 text-brand-red dark:text-brand-cyan" />
            Tool Consolidation ROI Calculator
          </h3>
          <p className="text-slate-600 dark:text-slate-400 font-medium mb-8">
            Estimate your potential savings by escaping the $80K Tool Trap™. Adjust the parameters below to see the impact of strategic consolidation.
          </p>

          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Current Marketing Tools</label>
                <span className="text-sm font-bold text-brand-dark dark:text-white">{tools}</span>
              </div>
              <input
                type="range"
                min="5"
                max="50"
                value={tools}
                onChange={(e) => setTools(Number(e.target.value))}
                className="w-full accent-brand-red dark:accent-brand-cyan"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Avg. Annual Cost Per Tool</label>
                <span className="text-sm font-bold text-brand-dark dark:text-white">${avgCost.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="1000"
                max="20000"
                step="500"
                value={avgCost}
                onChange={(e) => setAvgCost(Number(e.target.value))}
                className="w-full accent-brand-red dark:accent-brand-cyan"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Estimated Consolidation (%)</label>
                <span className="text-sm font-bold text-brand-dark dark:text-white">{consolidationRate}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="60"
                value={consolidationRate}
                onChange={(e) => setConsolidationRate(Number(e.target.value))}
                className="w-full accent-brand-red dark:accent-brand-cyan"
              />
              <p className="text-[10px] text-slate-400 mt-2">SEVEN HATS® methodology typically identifies 30-40% redundancy.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col justify-center bg-white dark:bg-brand-dark p-6 md:p-8 rounded-xl border border-slate-200 dark:border-slate-800 text-center">
          <span className="text-xs uppercase tracking-widest text-brand-yellow font-bold block mb-4">Strategic Impact</span>
          <div className="mb-6">
            <span className="text-slate-500 text-sm block mb-1">Estimated Annual Savings</span>
            <span className="text-5xl font-heading font-bold text-brand-red dark:text-brand-cyan">${estimatedSavings.toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-100 dark:border-slate-800 mb-6">
            <div>
              <span className="text-slate-500 text-xs block mb-1">Current Spend</span>
              <span className="text-lg font-bold text-brand-dark dark:text-white">${totalCurrentSpend.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-slate-500 text-xs block mb-1">Tools Eliminated</span>
              <span className="text-lg font-bold text-brand-dark dark:text-white">{toolsEliminated} redundant apps</span>
            </div>
          </div>

          {status === 'success' ? (
            <div className="p-4 bg-brand-cyan/10 border border-brand-cyan/20 rounded-lg">
              <p className="text-brand-cyan font-bold text-sm">Audit Request Sent!</p>
              <p className="text-slate-400 text-xs mt-1">We'll be in touch shortly.</p>
              <button
                onClick={resetFormStatus}
                className="mt-3 text-xs font-bold text-brand-cyan hover:text-white transition-colors"
              >
                Calculate Again
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 mt-auto">
              {status === 'error' && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-xs font-medium mb-2">
                  {error || 'Failed to submit. Please try again.'}
                </div>
              )}
              {showEmailInput && (
                <>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:border-brand-cyan outline-none rounded-lg text-sm dark:text-white"
                  />
                  <input
                    type="url"
                    name="websiteUrl"
                    required
                    placeholder="Enter your website URL"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:border-brand-cyan outline-none rounded-lg text-sm dark:text-white"
                  />
                </>
              )}
              <button type="submit" disabled={status === 'loading'} className="px-6 py-3 bg-brand-red text-white text-xs uppercase font-bold tracking-widest rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50">
                {status === 'loading' ? 'Sending...' : 'Get a Strategic Audit'}
              </button>
            </div>
          )}
        </form>

      </div>
    </div>
  );
};

export default ROICalculator;